import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge, EmptyState, QueryStateBoundary } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import {
  bookingSourcesCsvHref,
  useBookingSourcesReport,
  useDashboardSummary,
} from "@/api/hooks/useDashboard";
import { STAGES, STAGE_LABELS } from "@/api/types/inquiry";
import { PACKAGE_CODE_LABELS } from "@/api/types/package";
import { formatShortDate } from "@/features/events/eventDate";
import type {
  BookingSourceRow,
  DashboardSummary,
  PackageMixRow,
  PipelineStageRow,
  TargetPct,
  UpcomingTask,
} from "@/api/types/dashboard";

/**
 * Phase 8's owner home screen — replaces the Phase 0-7 placeholder. Reads
 * GET /v2/dashboard/summary (hero stats, pipeline, package mix, upcoming
 * tasks) plus the booking-sources report (also backs a CSV download).
 * Every number here is server-computed; this page only formats
 * (centsToUsd) and lays out — it never re-derives a KPI from raw rows.
 *
 * Visual language: the numbers are the display moment (Fraunces figures on
 * brass-lit stat tiles); section titles are set like a menu's course
 * headers — quiet caps that let the content carry the weight.
 */
export function DashboardPage() {
  const { data: summary, isLoading, error, refetch } = useDashboardSummary();
  const {
    data: bookingSources,
    isLoading: sourcesLoading,
    error: sourcesError,
    refetch: refetchSources,
  } = useBookingSourcesReport();

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-text-muted">
          {today}
        </p>
        <h1 className="mt-1 font-display text-2xl font-bold text-text-primary">Dashboard</h1>
        <p className="mt-1 text-sm text-text-secondary">
          The morning read: pipeline, package mix, booking sources, and what's due.
        </p>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {summary && (
          <div className="flex flex-col gap-6">
            <HeroStats summary={summary} />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <PipelineCard byStage={summary.pipeline.by_stage} />
              <PackageMixCard mix={summary.package_mix} target={summary.package_mix_target_pct} />
            </div>

            <BookingSourcesCard
              rows={bookingSources}
              isLoading={sourcesLoading}
              error={sourcesError}
              onRetry={() => void refetchSources()}
            />

            <UpcomingTasksCard tasks={summary.upcoming_tasks} />
          </div>
        )}
      </QueryStateBoundary>
    </div>
  );
}

// ---- Section shell -------------------------------------------------------------

/** Shared card shell: menu-style caps title, optional sub-line and aside. */
function SectionCard({
  title,
  sub,
  aside,
  testId,
  children,
}: {
  title: string;
  sub?: string;
  aside?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section
      className="rounded-xl border border-border bg-bg-card p-5 shadow-sm sm:p-6"
      data-testid={testId}
    >
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div>
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-text-secondary">
            {title}
          </h2>
          {sub && <p className="mt-1 text-xs text-text-muted">{sub}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

// ---- Hero stat tiles ---------------------------------------------------------

function HeroStats({ summary }: { summary: DashboardSummary }) {
  const { review_count, sla, this_week_events } = summary;
  const reviewGoalMet = review_count.total >= review_count.target;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-hero">
      <StatTile
        testId="stat-week-events"
        label="This week's events"
        value={String(this_week_events)}
      />
      <StatTile
        testId="stat-review-count"
        label="Reviews toward 20"
        value={`${review_count.total} of ${review_count.target}`}
        badge={{
          label: reviewGoalMet ? "Goal reached" : `${review_count.remaining} to go`,
          variant: reviewGoalMet ? "success" : "secondary",
        }}
      />
      <StatTile
        testId="stat-sla"
        label="Answered within 2h"
        value={`${sla.pct}%`}
        sub={`${sla.answered_within_2h} of ${sla.total_inquiries} inquiries`}
      />
    </div>
  );
}

function StatTile({
  testId,
  label,
  value,
  sub,
  badge,
}: {
  testId: string;
  label: string;
  value: string;
  sub?: string;
  badge?: { label: string; variant: "success" | "secondary" };
}) {
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-border bg-bg-card p-5 shadow-sm"
      data-testid={testId}
    >
      {/* brass shelf-light along the top edge */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-brass/80 via-brass/25 to-transparent"
      />
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-text-muted">
          {label}
        </p>
        {badge && (
          <Badge variant={badge.variant} size="sm">
            {badge.label}
          </Badge>
        )}
      </div>
      <p
        className="tabular mt-3 font-display text-3xl font-semibold leading-none tracking-tight text-text-primary sm:text-4xl"
        data-testid={`${testId}-value`}
      >
        {value}
      </p>
      {sub && <p className="mt-2 text-xs text-text-muted">{sub}</p>}
    </div>
  );
}

// ---- Pipeline by stage --------------------------------------------------------

function PipelineCard({ byStage }: { byStage: PipelineStageRow[] }) {
  const byStageMap = new Map(byStage.map((row) => [row.stage, row]));
  const maxValueCents = Math.max(1, ...byStage.map((row) => row.value_cents));

  return (
    <SectionCard title="Pipeline value by stage" testId="pipeline-card">
      <div className="flex flex-col gap-2.5">
        {STAGES.map((stage) => {
          const row = byStageMap.get(stage);
          const count = row?.count ?? 0;
          const valueCents = row?.value_cents ?? 0;
          const pct = maxValueCents > 0 ? Math.round((valueCents / maxValueCents) * 100) : 0;

          return (
            <div key={stage} className="flex items-center gap-3" data-testid={`pipeline-row-${stage}`}>
              <span className="w-24 shrink-0 text-xs text-text-secondary">{STAGE_LABELS[stage]}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-bg-muted">
                <div className="h-full rounded-full bg-copper" style={{ width: `${pct}%` }} />
              </div>
              <span className="tabular w-8 shrink-0 text-right text-xs text-text-muted">{count}</span>
              <span className="tabular w-20 shrink-0 text-right text-xs font-semibold text-text-primary">
                {centsToUsd(valueCents)}
              </span>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ---- Package mix vs target -----------------------------------------------------

function PackageMixCard({ mix, target }: { mix: PackageMixRow[]; target: TargetPct }) {
  const targetForCode: Partial<Record<PackageMixRow["code"], number>> = {
    signature: target.signature,
    classic: target.classic,
    story: target.story,
  };

  return (
    <SectionCard title="Package mix vs. target" testId="package-mix-card">
      {mix.length === 0 ? (
        <p className="text-sm text-text-muted">No bookings yet.</p>
      ) : (
        <div className="flex flex-col gap-3.5">
          {mix.map((row) => {
            const t = targetForCode[row.code];
            const atOrOverTarget = t !== undefined && row.pct >= t;

            return (
              <div key={row.code} data-testid={`package-mix-row-${row.code}`}>
                <div className="mb-1.5 flex items-center justify-between text-xs">
                  <span className="font-medium text-text-primary">{PACKAGE_CODE_LABELS[row.code]}</span>
                  <span className="tabular text-text-muted">
                    {row.pct}%{t !== undefined ? ` (target ${t}%)` : ""} · {row.booked_count} booked
                  </span>
                </div>
                <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-bg-muted">
                  <div
                    className={`h-full rounded-full ${atOrOverTarget ? "bg-success" : "bg-copper"}`}
                    style={{ width: `${Math.min(100, row.pct)}%` }}
                  />
                  {t !== undefined && (
                    <div
                      className="absolute top-0 h-full w-0.5 bg-text-primary/60"
                      style={{ left: `${Math.min(100, t)}%` }}
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// ---- Booking sources -----------------------------------------------------------

/** Coaster-sized bar-chart glyph for the no-data state (replaces the stock emoji). */
function ChartGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7 text-copper"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 20h16" />
      <path d="M7 16.5v-4" />
      <path d="M12 16.5v-7" />
      <path d="M17 16.5v-10" />
    </svg>
  );
}

function BookingSourcesCard({
  rows,
  isLoading,
  error,
  onRetry,
}: {
  rows: BookingSourceRow[] | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <SectionCard
      title="Booking sources"
      sub="Which channel actually books events — not just which one generates inquiries."
      testId="booking-sources-card"
      aside={
        <a
          href={bookingSourcesCsvHref}
          download
          className="rounded-sm text-xs font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          data-testid="booking-sources-csv-link"
        >
          Download CSV
        </a>
      }
    >
      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={onRetry}>
        {rows && rows.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-border" data-testid="booking-sources-table">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-bg-hover/50">
                <tr>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2">Inquiries</th>
                  <th className="px-4 py-2">Booked</th>
                  <th className="px-4 py-2">Close rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row) => (
                  <tr key={row.source}>
                    <td className="px-4 py-3 font-medium text-text-primary">{row.source}</td>
                    <td className="tabular px-4 py-3 text-text-secondary">{row.inquiries}</td>
                    <td className="tabular px-4 py-3 text-text-secondary">{row.booked}</td>
                    <td className="tabular px-4 py-3 text-text-secondary">{row.close_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<ChartGlyph />}
            title="No inquiries logged yet"
            description="Booking-source data appears once inquiries start coming in."
          />
        )}
      </QueryStateBoundary>
    </SectionCard>
  );
}

// ---- Upcoming this week --------------------------------------------------------

/** Check glyph for the all-clear state — same coaster treatment as ChartGlyph. */
function CheckGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7 text-copper"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5 10 17.5 19 7" />
    </svg>
  );
}

function UpcomingTasksCard({ tasks }: { tasks: UpcomingTask[] }) {
  return (
    <SectionCard title="Upcoming this week" testId="upcoming-tasks-card">
      {tasks.length === 0 ? (
        <EmptyState
          icon={<CheckGlyph />}
          title="Nothing due this week"
          description="T-minus tasks and touches show up here as they come due."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border" data-testid="upcoming-tasks-list">
          {tasks.map((task, i) => (
            <li
              key={`${task.title}-${task.due_date ?? "none"}-${i}`}
              className="flex flex-wrap items-center justify-between gap-3 py-2.5 text-sm"
              data-testid="upcoming-task-row"
            >
              <div className="flex flex-col">
                <span className="font-medium text-text-primary">{task.title}</span>
                <span className="text-xs text-text-muted">{task.kind}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="tabular text-xs text-text-secondary">
                  {formatShortDate(task.due_date)}
                </span>
                {task.event_id && (
                  <Link
                    to={`/events/${task.event_id}`}
                    className="rounded-sm text-xs font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    View event
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
